import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';

const Seo = ({
  title,
  description,
  canonicalUrl,
  prevPageUrl,
  nextPageUrl,
  ogImage,
  ogType = 'website',
  jsonLd = null,
  additionalMeta = [],
  siteUrl = 'https://www.promocionalesenstock.com',
  locale = 'es_CO',
  siteName = 'Promocionales en Stock',
  twitterHandle = '@promocionalesenstock'
}) => {
  const absoluteUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${siteUrl}${url}`;
  };

  const fullCanonicalUrl = absoluteUrl(canonicalUrl);
  const fullPrevPageUrl = absoluteUrl(prevPageUrl);
  const fullNextPageUrl = absoluteUrl(nextPageUrl);
  const fullOgImage = absoluteUrl(ogImage || '/images/og-default.jpg');

  return (
    <Helmet>
      {/* Charset y viewport */}
      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
      
      {/* Title y Description */}
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}

      {/* URLs canónicas y paginación */}
      {fullCanonicalUrl && <link rel="canonical" href={fullCanonicalUrl} />}
      {fullPrevPageUrl && <link rel="prev" href={fullPrevPageUrl} />}
      {fullNextPageUrl && <link rel="next" href={fullNextPageUrl} />}

      {/* Open Graph completo */}
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      {fullCanonicalUrl && <meta property="og:url" content={fullCanonicalUrl} />}
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:type" content={ogType} />
      <meta property="og:locale" content={locale} />
      <meta property="og:site_name" content={siteName} />

      {/* Twitter Card mejorado */}
      <meta name="twitter:card" content="summary_large_image" />
      {twitterHandle && <meta name="twitter:site" content={twitterHandle} />}
      {twitterHandle && <meta name="twitter:creator" content={twitterHandle} />}
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={fullOgImage} />

      {/* Meta tags para SEO móvil y AI */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="theme-color" content="#c8102e" />
      
      {/* Meta para Google */}
      <meta name="google" content="notranslate" />
      <meta name="googlebot" content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" />
      <meta name="bingbot" content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" />
      
      {/* Meta adicionales */}
      {additionalMeta.map((m, idx) => (
        <meta key={`additional-meta-${idx}`} name={m.name} content={m.content} />
      ))}

      {/* JSON-LD Schema */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd, null, 2)}
        </script>
      )}
      
      {/* Preconnect para optimización de velocidad */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="dns-prefetch" href="//www.google-analytics.com" />
      <link rel="dns-prefetch" href="//www.googletagmanager.com" />
      
      {/* Favicon */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
    </Helmet>
  );
};

Seo.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  canonicalUrl: PropTypes.string,
  prevPageUrl: PropTypes.string,
  nextPageUrl: PropTypes.string,
  ogImage: PropTypes.string,
  ogType: PropTypes.string,
  jsonLd: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  additionalMeta: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      content: PropTypes.string.isRequired
    })
  ),
  siteUrl: PropTypes.string,
  locale: PropTypes.string,
  siteName: PropTypes.string,
  twitterHandle: PropTypes.string
};

Seo.defaultProps = {
  title: '',
  description: '',
  canonicalUrl: '',
  prevPageUrl: null,
  nextPageUrl: null,
  ogImage: '',
  ogType: 'website',
  jsonLd: null,
  additionalMeta: [],
  siteUrl: process.env.REACT_APP_SITE_URL || 'https://www.promocionalesenstock.com',
  locale: 'es_CO',
  siteName: 'Promocionales en Stock',
  twitterHandle: '@promocionalesenstock'
};

export default Seo;